import React from 'react';
import { ArrowDownToLine, CircleAlert } from 'lucide-react';
import NoDataFound from '../../component/noDataComponent/NoDataFound';

const SkeletonRows = ({ rows = 5 }) => {
  return Array.from({ length: rows }).map((_, idx) => (
    <tr key={`sk-${idx}`} className="upload-history-skeleton-row">
      <td><div className="skeleton-line" style={{width: '20px'}} /></td>
      <td><div className="skeleton-line" style={{width: '140px'}} /></td>
      <td><div className="skeleton-line" style={{width: '200px'}} /></td>
      <td><div className="skeleton-badge" style={{width: '80px'}} /></td>
      <td><div className="skeleton-line" style={{width: '60px'}} /></td>
      <td><div className="skeleton-actions" /></td>
    </tr>
  ));
};

const UploadHistoryTable = ({
  loading = false,
  uploadHistory = [],
  totalCount = 0,
  currentPage = 1,
  pageSize = 10,
  totalPages = 1,
  pageWindow = [],
  onDownloadErrorReport,
  onViewValidation,
  onPageChange,
}) => {
  const getStatusClass = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "success":
      case "completed":
        return "upload-status upload-status-success";
      case "failed":
      case "error":
        return "upload-status upload-status-failed";
      case "processing":
      case "pending":
        return "upload-status upload-status-processing";
      default:
        return "upload-status upload-status-default";
    }
  };

  const getDisplayStatus = (status) => {
    const statusMap = {
      success: "Success",
      completed: "Success",
      failed: "Failed",
      error: "Failed",
      processing: "Processing",
      pending: "Processing",
    };
    return statusMap[status?.toLowerCase()] || status;
  };

  const showingFrom = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="upload-history-table-wrapper">
      <div className="upload-history-table-container">
        <table className="upload-history-table">
          <thead>
            <tr>
              <th>SNo</th>
              <th>Upload Date/Time</th>
              <th>File Name</th>
              <th>Status</th>
              {/* <th>Total Records</th> */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows rows={pageSize} />
            ) : uploadHistory.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data-row">
                  <NoDataFound />
                </td>
              </tr>
            ) : (
              uploadHistory.map((item, index) => (
                <tr key={item.id} className="upload-history-row">
                  <td className="sno-cell">{(currentPage - 1) * pageSize + index + 1}</td>
                  <td>
                    <div className="date-time-cell">
                      <span>
                        {item.created_at
                          ? new Date(item.created_at).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="file-name-cell">
                    {item.original_file_name || "Unknown file"}
                  </td>
                  <td>
                    <span className={getStatusClass(item.status)}>
                      <div className="status-dot"></div>
                      {getDisplayStatus(item.status)}
                    </span>
                  </td>
                  {/* <td>{item.total_rows?.toLocaleString() || "0"}</td> */}
                  <td>
                    <div className="actions-cell">
                      <button
                        className={`action-btn download-btn ${
                          item.status?.toLowerCase() === "pending" ||
                          item.status?.toLowerCase() === "processing"
                            ? "disabled"
                            : ""
                        }`}
                        onClick={() =>
                          item.status?.toLowerCase() !== "pending" &&
                          item.status?.toLowerCase() !== "processing" &&
                          onDownloadErrorReport(item.id, item.excel_file_name)
                        }
                        title={
                          item.status?.toLowerCase() === "pending" ||
                          item.status?.toLowerCase() === "processing"
                            ? "Processing..."
                            : "Download Error Report"
                        }
                        disabled={
                          item.status?.toLowerCase() === "pending" ||
                          item.status?.toLowerCase() === "processing"
                        }
                      >
                        <ArrowDownToLine size={16} />
                        {(item.status?.toLowerCase() === "pending" ||
                          item.status?.toLowerCase() === "processing") && (
                          <span className="processing-indicator"></span>
                        )}
                      </button>
                      {(item.status?.toLowerCase() === "failed" ||
                        item.status?.toLowerCase() === "error") && (
                        <button
                          className="action-btn view-btn"
                          onClick={() => onViewValidation(item.id)}
                          title="View Validation Errors"
                        >
                          <CircleAlert size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="upload-history-pagination">
        <div className="pagination-info">
          {loading ? (
            <>Loading…</>
          ) : (
            <>
              Showing {showingFrom} to {showingTo} of {totalCount} results
            </>
          )}
        </div>

        <div className="pagination-buttons">
          <button
            className="pagination-btn"
            disabled={loading || currentPage === 1}
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          >
            Previous
          </button>

          {pageWindow.map((p, idx) =>
            typeof p === "string" ? (
              <button key={p + idx} className="pagination-btn ellipsis-btn" disabled>
                …
              </button>
            ) : (
              <button
                key={p}
                className={`pagination-btn ${p === currentPage ? "active" : ""}`}
                onClick={() => onPageChange(p)}
                disabled={loading}
              >
                {p}
              </button>
            )
          )}

          <button
            className="pagination-btn"
            disabled={loading || currentPage >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadHistoryTable;
